// @flow

import React, { type Node } from 'react';
import { connect } from 'react-redux';

import AdTarget from '@ta/cpm.ad-target-for-modal';
import { listenToCpmMessage, COLLAPSER_ADVERTISER_ID } from '@ta/cpm.adsupport';
import trackEventAJAX, { ThirdPartyTracking } from '@ta/common.tracking';
import Radio from '@ta/common.radio';
import { featureIsEnabled } from '@ta/common.features';

const GEOPILL_COLLAPSER_ADVERTISER_ID = 138287437384;

// geo pill ad use a custom collapser id
// but still check for the general collapser id during the ID transition (ADS-6127)
const COLLAPSER_IDS = [COLLAPSER_ADVERTISER_ID, GEOPILL_COLLAPSER_ADVERTISER_ID];

/** GeoPillSponsorData : data used by GeoPillSponsorContext */
export type GeoPillSponsorData = {|
  /** Location name */
  name: string,

  /** Location id */
  location_id: number,

  /** Location parents, ordered from closest */
  ancestors?: Array<string>,

  /** URL provided by DFP for tracking (redirecting when user clicks) */
  dfpTrackingUrl?: string,

  /** URL provided by DFP for third-party click tracking */
  thirdPartyClickTrackerURL?: string,
|};

/** provider function for geo pill sponsor data */
export type GeoPillSponsorDataProvider = ((GeoPillSponsorData) => void) => void;

type ExternalProps = {|
  /** Identifier for the ads area */
  adAreaId: string,

  /** React node interested on GeoPillSponsor context */
  children: (sponsorDataProvider?: ?GeoPillSponsorDataProvider) => Node,
|};

type Props = {|
  ...ExternalProps,

  /** page servlet for geopill sponsored result feature check */
  pageServlet?: ?string,
|};

type State = {|
  sponsorData?: ?GeoPillSponsorData,
  dfpRequested: number,
  adTargetRadio: Radio,
  dfpEndedEventTracked: boolean,
  listeners: Array<(GeoPillSponsorData) => void>,
  impressionTrackerURL: string,
|};

class GeoPillSponsor extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      dfpRequested: 0,
      adTargetRadio: Radio(`geopill-sponsor-${props.adAreaId}`),
      dfpEndedEventTracked: false,
      listeners: [],
      impressionTrackerURL: '',
    };
  }

  updateSponsorData = (sponsorData: GeoPillSponsorData, reason: string) => {
    this.state.listeners.forEach(listener => listener(sponsorData));
    this.setState({ listeners: [], sponsorData });

    trackEventAJAX({
      module: 'SponsoredWhereTo',
      action: 'sponsor_loaded',
      context: `${reason}|${this.props.adAreaId}`,
      nonInteractive: true,
      value: Date.now() - this.state.dfpRequested,
    });
  };

  trackDFPLoad = (reason: string) => {
    trackEventAJAX({
      module: 'SponsoredWhereTo',
      action: 'dfp_responded',
      context: `${reason}|${this.props.adAreaId}`,
      nonInteractive: true,
      value: Date.now() - this.state.dfpRequested,
    });
  };

  isSponsoredGeopillEnabled = () => featureIsEnabled('where_to_top_featured_destination');

  // Check DoubleClick response for the collapser or empty response (dev)
  checkDoubleClickResponse = (event: {
    isEmpty: boolean,
    advertiserId: number,
    creativeId: string,
  }) => {
    if (!this.state.dfpEndedEventTracked) {
      this.setState({ dfpEndedEventTracked: true });

      if (event && !event.isEmpty) {
        if (COLLAPSER_IDS.includes(event.advertiserId)) {
          this.trackDFPLoad('dfp_collapser');
        } else {
          this.trackDFPLoad('dfp_response');
        }
      } else {
        this.trackDFPLoad('dfp_empty');
      }
    }
  };

  fetchAds = () => {
    if (!this.state.dfpRequested && this.isSponsoredGeopillEnabled()) {
      this.setState({ dfpRequested: Date.now() });

      listenToCpmMessage('whereToSponsor', data => {
        // make sure that we don't override sponsorData which may have been set by some async event
        if (!this.state.sponsorData) {
          const {
            sponsorName,
            sponsorParentGeoName,
            pinGeoId,
            clickTracker,
            thirdPartyClickTrackerURL,
            impressionTrackerURL,
          } = data;

          this.updateSponsorData(
            {
              name: sponsorName,
              location_id: pinGeoId,
              ancestors: (sponsorParentGeoName || '').split(',').map(i => i.trim()),
              dfpTrackingUrl: clickTracker,
              thirdPartyClickTrackerURL,
            },
            'dfp_sponsor',
          );
          this.setState({ impressionTrackerURL });
        }
      });

      // eslint-disable-next-line no-restricted-globals
      window.googletag = window.googletag || { cmd: [] };

      // Listen for DoubleClick Events
      // eslint-disable-next-line no-restricted-globals
      window.googletag.cmd.push(() => {
        // eslint-disable-next-line no-restricted-globals
        window.googletag
          .pubads()
          .addEventListener('slotRenderEnded', this.checkDoubleClickResponse);
      });

      this.state.adTargetRadio.emit('refreshAds');
    }
  };

  sponsorDataProvider = (callback: GeoPillSponsorData => void): void => {
    if (this.state.sponsorData) {
      if (typeof callback === 'function') {
        callback(this.state.sponsorData);
      }
    } else {
      this.fetchAds();

      if (typeof callback === 'function') {
        this.state.listeners.push(callback);
      }
    }
  };

  render() {
    const { adTargetRadio } = this.state;
    const { children, adAreaId } = this.props;

    return (
      <>
        {this.isSponsoredGeopillEnabled() ? (
          <React.Fragment>
            <AdTarget size="3x1" position={adAreaId} defer hidden radio={adTargetRadio} />
            <ThirdPartyTracking url={this.state.impressionTrackerURL} />
          </React.Fragment>
        ) : null}
        {children(this.isSponsoredGeopillEnabled() ? this.sponsorDataProvider : null)}
      </>
    );
  }
}

export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  pageServlet: state.route.page,
}))(GeoPillSponsor);
